import React from 'react';
import {
    Box, Typography, Card, CardContent, Container, Chip,
    useTheme, useMediaQuery, Button, CircularProgress
} from "@mui/material";
import { Stethoscope, Calendar, User, CheckCircle, Clock, FileText } from 'lucide-react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import CustomAppBar from "../../components/CustomAppBar";
import PortalFabMenu from "../../components/PortalFabMenu";
import PortalHeader from "../../components/PortalHeader";
import { useNavigate, useLocation, useNavigationType } from 'react-router-dom';
import { formatAssessmentText, getAssessmentText, hasData, getChipStatus } from '../../lib/formatting';
import { typographySx } from "../../lib/uiformatting";
import { callBackendApiGet } from "../../lib/api";
import { updatePortalData } from '../../redux/portalSlice';
import { setPortal, setEmail, setPhoneNo } from '../../redux/userSlice';
import { useAuth0 } from "@auth0/auth0-react";
import logDrtEvent from '../../lib/logging';

export default function CaseList() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();
    const navigationType = useNavigationType();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

    const { getAccessTokenSilently, user, isAuthenticated, isLoading } = useAuth0();

    const [loading, setLoading] = React.useState(false);
    const [showAllCases, setShowAllCases] = React.useState(false);

    const allCases = useSelector(s => s.portal.allCases, shallowEqual);
    const INITIAL_CASES_TO_SHOW = 3;
    const visibleCases = showAllCases ? allCases : allCases.slice(0, INITIAL_CASES_TO_SHOW);
    const hasMoreCases = allCases.length > INITIAL_CASES_TO_SHOW;
    const userFirstName = useSelector(s => s.user.firstName);
    const userEmail = useSelector(s => s.user.email);
    const userPhone = useSelector(s => s.user.phoneNo);
    const userConcern = useSelector(s => s.user.concern);
    const cameViaBottomNav = location.state?.viaBottomNav === true;
    const arrivedViaInAppNav = cameViaBottomNav && (navigationType === 'PUSH' || navigationType === 'REPLACE');
    const initialSkipRef = React.useRef(arrivedViaInAppNav);
    const didFetchRef = React.useRef(false);

    React.useEffect(() => {
        if (cameViaBottomNav) {
            navigate(location.pathname + location.search, { replace: true, state: {} });
        }
    }, [cameViaBottomNav, navigate, location.pathname, location.search]);

    React.useEffect(() => {
        dispatch(setPortal(true));
    }, [dispatch]);

    
    const fetchPortalData = React.useCallback(async ({ bypassGuard = false } = {}) => {
        const phoneNumber = user?.phone_number;
        if (!phoneNumber) { 
            return;
        }
        if (didFetchRef.current && !bypassGuard) return;
        if (!bypassGuard) didFetchRef.current = true;

        try {
            setLoading(true);
            const token = await getAccessTokenSilently({
                authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE }
            });
            const res = await callBackendApiGet(
                `/api/v1/portal/patient?phone=${encodeURIComponent(phoneNumber)}`,
                {},
                token
            );
            if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
            const data = await res.json();

            if (Array.isArray(data) && data.length > 0) {
                dispatch(updatePortalData({ patientData: data }));
                const patient = data[0];
                if (patient) {
                    if (patient?.contact?.email) dispatch(setEmail(patient?.contact?.email));
                    if (patient?.contact?.phone) dispatch(setPhoneNo(patient?.contact?.phone));
                }
            }
        } catch (err) {
            logDrtEvent(`Error while fetching portal information for patients with phone: ${err}`);
        } finally {
            setLoading(false);
        }
    }, [user, dispatch, getAccessTokenSilently]);

    React.useEffect(() => {
        if (isLoading) return;
        if (!isAuthenticated) return;

        if (initialSkipRef.current) {
            initialSkipRef.current = false;
            didFetchRef.current = true;
            setLoading(false);
            return;
        }

        fetchPortalData();
    }, [isLoading, isAuthenticated, fetchPortalData]);

    const handleCaseClick = (index) => {
        const c = allCases[index];
        navigate(`/case-details/${c.patientID}/${c.id}`, {
            state: { viaCaseList: true },
        });
    };

    const containerStyle = {
        maxWidth: isDesktop ? '1000px' : 'sm',
        pt: isDesktop ? 4 : 2,
        pb: isDesktop ? 4 : 2,
        px: isDesktop ? 4 : 4,
    };


    return (
        <Box sx={{ 
            pb: 7, 
            minHeight: '100vh', 
            background: 'linear-gradient(to bottom right, rgba(239, 246, 255, 0.4), rgba(240, 253, 250, 0.3), rgba(255, 247, 237, 0.2))',
        }}>
            <CustomAppBar noBack={true} returning={false} portal={true} />

            <Container sx={containerStyle}>
                <PortalHeader 
                    userFirstName={userFirstName}
                    userEmail={userEmail}
                    userPhone={userPhone}
                    user={user}
                />

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{
                            backgroundColor: '#1a3a5c',
                            borderRadius: 2,
                            p: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}>
                            <Stethoscope color="white" size={25} />
                        </Box>
                        <Box>
                            <Typography variant="h7" component="div" sx={{ ...typographySx, mb: 0, fontSize: '1.4rem' }}>
                                My Visits
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                Your dermatology appointments and treatments
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {(isLoading || loading) ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
                        <CircularProgress color="secondary" />
                    </Box>
                ) : (
                    <>
                        {allCases.length > 0 ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                                {visibleCases.map((caseItem, index) => {
                                    const isComplete = getChipStatus(caseItem);
                                    return (
                                        <Card
                                            key={caseItem.id}
                                            elevation={1}
                                            onClick={() => handleCaseClick(index)}
                                            sx={{
                                                width: isDesktop ? '600px' : '100%',
                                                p: 0,
                                                borderRadius: '12px',
                                                cursor: 'pointer',
                                                background: isComplete
                                                    ? 'linear-gradient(to bottom right, rgba(240, 253, 250, 0.8), rgba(236, 253, 245, 0.6))'
                                                    : 'linear-gradient(to bottom right, rgba(255, 247, 237, 0.8), rgba(253, 242, 248, 0.6))',
                                                border: '1px solid',
                                                borderColor: isComplete ? '#ccfbf1' : '#ffedd5',
                                                boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
                                                transition: 'all 0.2s',
                                                '&:hover': {
                                                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                                                    borderColor: isComplete ? '#99f6e4' : '#fed7aa',
                                                },
                                            }}
                                        >
                                            <CardContent sx={{ p: 2.5 }}>
                                                {/* Header with title and status chip */}
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                                                    <Typography
                                                        variant="h6"
                                                        sx={{
                                                            fontWeight: 700,
                                                            color: '#1a1a2e',
                                                            fontSize: '1.1rem',
                                                            flex: 1,
                                                            pr: 1,
                                                        }}
                                                    >
                                                        {caseItem.patient_name}
                                                    </Typography>
                                                    <Chip
                                                        icon={isComplete
                                                            ? <CheckCircle size={14} color="#0d9488" />
                                                            : <Clock size={14} color="#ea580c" />
                                                        }
                                                        label={isComplete ? "Complete" : "In Progress"}
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: isComplete ? '#ccfbf1' : '#ffedd5',
                                                            color: isComplete ? '#0d9488' : '#ea580c',
                                                            fontWeight: 600,
                                                            fontSize: '0.75rem',
                                                            borderRadius: '16px',
                                                            '& .MuiChip-icon': {
                                                                marginLeft: '8px',
                                                            },
                                                        }}
                                                    />
                                                </Box>

                                                {/* Description */}
                                                {hasData(caseItem) && (
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            color: '#5a5a6e',
                                                            mb: 2,
                                                            lineHeight: 1.5,
                                                        }}
                                                    >
                                                        {userConcern || formatAssessmentText(getAssessmentText(caseItem))}
                                                    </Typography>
                                                )}

                                                {/* Date row */}
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                    <Calendar
                                                        size={16}
                                                        color={isComplete ? '#5a5a6e' : '#ea580c'}
                                                    />
                                                    <Typography variant="body2" sx={{ color: '#5a5a6e' }}>
                                                        {new Date(caseItem.submitted_at).toLocaleDateString('en-US', {
                                                            month: 'short',
                                                            day: 'numeric',
                                                            year: 'numeric'
                                                        })}
                                                    </Typography>
                                                </Box>

                                                {/* Doctor/Provider row */}
                                                {caseItem.recommended_follow_up && hasData(caseItem) && (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <User
                                                            size={16}
                                                            color={isComplete ? '#5a5a6e' : '#ea580c'}
                                                        />
                                                        <Typography variant="body2" sx={{ color: '#5a5a6e' }}>
                                                            {caseItem.recommended_follow_up === "As needed"
                                                                ? "Follow up as needed"
                                                                : `Follow up in ${caseItem.recommended_follow_up}`}
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </CardContent>
                                        </Card>
                                    );
                                })}

                                {/* Show more button */}
                                {hasMoreCases && !showAllCases && (
                                    <Button
                                        variant="outlined"
                                        onClick={() => setShowAllCases(true)}
                                        sx={{
                                            mt: 1,
                                            borderRadius: '20px',
                                            borderColor: '#5a5a6e',
                                            color: '#5a5a6e',
                                            fontWeight: 600,
                                            textTransform: 'none',
                                            px: 3,
                                            '&:hover': {
                                                borderColor: '#1a1a2e',
                                                backgroundColor: 'rgba(26, 26, 46, 0.04)',
                                            },
                                        }}
                                    >
                                        Show {allCases.length - INITIAL_CASES_TO_SHOW} more visit{allCases.length - INITIAL_CASES_TO_SHOW > 1 ? 's' : ''}
                                    </Button>
                                )}

                                {/* Show less button */}
                                {showAllCases && hasMoreCases && (
                                    <Button
                                        variant="text"
                                        onClick={() => setShowAllCases(false)}
                                        sx={{
                                            mt: 1,
                                            color: '#5a5a6e',
                                            fontWeight: 500,
                                            textTransform: 'none',
                                            '&:hover': {
                                                backgroundColor: 'rgba(26, 26, 46, 0.04)',
                                            },
                                        }}
                                    >
                                        Show Less
                                    </Button>
                                )}
                            </Box>
                        ) : (
                            <Container maxWidth="sm" sx={{ mt: 4 }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                                    {/* Icon */}
                                    <Box
                                        sx={{
                                            width: '64px',
                                            height: '64px',
                                            borderRadius: '12px',
                                            background: 'linear-gradient(to bottom right, rgba(239, 246, 255, 0.8), rgba(240, 253, 250, 0.6))',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mb: 3,
                                        }}
                                    >
                                        <FileText size={32} color="#1e40af" />
                                    </Box>

                                    {/* Heading */}
                                    <Typography
                                        variant="h5"
                                        sx={{
                                            fontWeight: 700,
                                            color: '#1a1a1a',
                                            fontSize: '1.2rem',
                                            mb: 1.5,
                                        }}
                                    >
                                        No Visits Yet
                                    </Typography>

                                    {/* Descriptive Paragraph */}
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            color: '#6b7280',
                                            fontSize: '0.84rem',
                                            mb: 3,
                                            maxWidth: '400px',
                                        }}
                                    >
                                        You haven't started any visits with Piction Health.
                                    </Typography>
                                </Box>
                            </Container>
                        )}
                    </>
                )}
            </Container>

            {/* Expandable FAB Menu */}
            {allCases.length > 0 && <PortalFabMenu navigate={navigate} />}
        </Box>
    );
}
